import React, { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  TablePagination,
  IconButton
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'

interface PaginatedGridProps {
  handleClose: () => void
  data: string[]
}

interface PaginationState {
  page: number
  pageSize: number
  totalItem: number
}

export const ViewInfoLpn: React.FC<PaginatedGridProps> = ({
  data,
  handleClose
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 5,
    totalItem: data.length
  })

  // Filtrar datos basados en la búsqueda
  const filteredData = useMemo(() => {
    if (!searchTerm) return data
    return data.filter((item) =>
      item.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [data, searchTerm])

  // Calcular datos paginados
  const paginatedData = useMemo(() => {
    const startIndex = pagination.page * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  // Actualizar total de items cuando cambian los datos filtrados
  React.useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      totalItem: filteredData.length
    }))
  }, [filteredData.length])

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 0 })) // Resetear a primera página al buscar
  }

  const handleClear = () => {
    setSearchTerm('')
    setPagination((prev) => ({ ...prev, page: 0 }))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      pageSize,
      totalItem: filteredData.length
    })
  }

  return (
    <Grid container spacing={2}>
      {/* Barra de búsqueda */}
      <Grid container item spacing={1} alignItems='center'>
        <Grid item xs={6}>
          <TextField
            label='Buscar'
            variant='outlined'
            size='small'
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </Grid>
        <Grid container item xs={6} spacing={1} alignItems='center'>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Buscar
            </Button>
          </Grid>
          <Grid item>
            <IconButton
              color='primary'
              onClick={handleClear}
              disabled={searchTerm.length === 0}
            >
              <RotateLeftIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>

      {/* Grilla */}
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography
                    variant='subtitle1'
                    style={{ fontWeight: 'bold' }}
                  >
                    LPN
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align='center'>
                    <Typography variant='body2' color='textSecondary'>
                      No se encontraron resultados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Paginación igual a GenericTable */}
      <Grid item xs={12}>
        {filteredData.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage='Filas por página'
            count={pagination.totalItem}
            page={pagination.page}
            rowsPerPage={pagination.pageSize}
            onPageChange={(_, page) =>
              handlePaginationChange(page, pagination.pageSize)
            }
            onRowsPerPageChange={(event) =>
              handlePaginationChange(0, parseInt(event.target.value as string))
            }
          />
        )}
      </Grid>

      <Grid container item xs={12} justifyContent='center'>
        <Grid item xs={12} md={12}>
          <Button
            fullWidth
            variant='contained'
            color='primary'
            onClick={() => handleClose()}
          >
            Cerrar
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}
